import Edit from '@mui/icons-material/Edit'
import { Box, Button, IconButton, Stack } from '@mui/material'
import { dropLast, last } from 'ramda'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FieldModel } from '@cibo/core'

import { LoadingButton } from '@mui/lab'
import { useSetLabels } from '../../../queries'
import { FieldLabel } from '../../FieldLabel'
import { LabelEditorField } from './LabelEditorField'

type FieldLabelEditorProps = {
  field: FieldModel
}

export const FieldLabelEditor = ({ field }: FieldLabelEditorProps) => {
  const { t } = useTranslation('@cibo/landmanager/components/Editors/LabelEditor')
  const saveLabels = useSetLabels()

  const [showEditor, setShowEditor] = useState(false)

  const [labels, setLabels] = useState<string[]>(field.labels)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [error, setError] = useState<Error | undefined>()

  const [initialLabels, setInitialLabels] = useState<string[]>()
  useEffect(() => {
    if (showEditor) {
      setInitialLabels(labels)
    }
  }, [showEditor])

  // This probably isn't strictly necessary, but the UI looks very weird when the edit icon is
  // an orphan, which is highly likely to happen if not explicitly prevented.
  const lastLabel = last(labels)

  const onSave = () => {
    saveLabels.mutateAsync(
      {
        resourceIds: [field.id],
        labels,
      },
      {
        onSuccess: () => {
          setError(undefined)
          setShowEditor(false)
        },
        onError: error => setError(error as Error),
      }
    )
  }

  return showEditor ? (
    <Stack pt={1} spacing={1}>
      <LabelEditorField onChange={labels => setLabels(labels || [])} />
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <Button
          variant="outlined"
          onClick={() => {
            setLabels(initialLabels || [])
            setShowEditor(false)
          }}
        >
          {t('cancel')}
        </Button>
        <LoadingButton loading={saveLabels.isLoading} variant="contained" onClick={onSave}>
          {t('save')}
        </LoadingButton>
      </Stack>
    </Stack>
  ) : (
    <Stack spacing={1}>
      <Box display="flex" flexDirection="row" flexWrap="wrap">
        {dropLast(1, labels).map((label: any) => (
          <FieldLabel color="secondary" label={label} key={label} sx={{ mt: 1, mr: 1 }} />
        ))}
        <Box>
          {lastLabel ? (
            <FieldLabel color="secondary" label={lastLabel} sx={{ mt: 1, mr: 1 }} />
          ) : (
            <FieldLabel sx={{ mt: 1, mr: 1 }} label={t('noLabels')} />
          )}
          <IconButton
            size="small"
            style={{ marginTop: 8 }}
            onClick={() => setShowEditor(true)}
            color="primary"
            aria-label={t('editLabel')}
          >
            <Edit fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    </Stack>
  )
}
